import { createContext, useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";

import { globalApi, setCurrentVessel } from "@/lib/api";
import { rememberVessel } from "@/lib/currentVessel";
import { useAsync } from "@/lib/hooks";
import type { VesselSummary } from "@/types";

/**
 * Binds the vessel in the URL to the one the API client sends.
 *
 * Everything under `/v/:vesselId` renders inside this. It sets the module-level
 * id in `lib/api` **before** any child mounts, so no request can go out against
 * a stale vessel — and it deliberately renders nothing until it has, rather
 * than letting one render pass fire off a round of requests for whichever
 * vessel was last selected. On a phone that pass is invisible; the readings it
 * would have fetched belong to the wrong pool.
 *
 * The id lives in the URL rather than in a context or local storage so that the
 * back button, a bookmark and a shared link all mean what they say. The two
 * places it is written — here, and cleared on unmount — are the whole surface.
 */
/**
 * Every vessel the household has, fetched once here rather than per screen.
 *
 * `AppShell` needs it only to decide whether a "back to the picker" control
 * earns its place — with one pool that control leads somewhere that redirects
 * straight back, which reads as broken. Fetching it in each screen's shell
 * would be a request per navigation for a question whose answer changes about
 * once a summer.
 */
const VesselsContext = createContext<VesselSummary[]>([]);

export function useVessels(): VesselSummary[] {
  return useContext(VesselsContext);
}

/** For tests that mount a screen without the route: the vessels it would see. */
export const VesselsProvider = VesselsContext.Provider;

export default function VesselRoute() {
  const { vesselId } = useParams<{ vesselId: string }>();
  const id = Number(vesselId);
  // Decoration: a failure here costs the switcher, never the screen.
  const vessels =
    useAsync<VesselSummary[]>(
      () => globalApi.get<VesselSummary[]>("/api/vessels").catch(() => []),
      [],
    ).data ?? [];

  // Bound during render, not in an effect. Children's effects run before a
  // parent's, so an effect here would let the first screen under a new id
  // fire its requests against the previous vessel. The client's id is module
  // state rather than React state, so setting it here is idempotent and safe.
  if (Number.isFinite(id)) setCurrentVessel(id);

  // Remembered so that pages with no pool in their URL — the guide, the
  // account page — can still point their bar at this one.
  useEffect(() => {
    if (Number.isFinite(id)) rememberVessel(id);
  }, [id]);

  useEffect(() => {
    // Cleared on the way out so that a stray request from an unmounting screen
    // fails loudly instead of resolving against whichever vessel is next.
    return () => setCurrentVessel(null);
  }, []);

  if (!Number.isFinite(id)) {
    return (
      <main className="mx-auto max-w-lg px-4 py-8">
        <p className="text-sm text-(--color-ink-muted)">That is not a pool this app knows about.</p>
      </main>
    );
  }

  // Keyed by the id so that switching pools in place remounts the screen
  // beneath. Without the key, React Router keeps the same screen instance
  // across a param change and every `useAsync` with empty deps keeps showing
  // the previous pool's data under the new pool's URL.
  return (
    <VesselsContext.Provider value={vessels}>
      <Outlet key={id} />
    </VesselsContext.Provider>
  );
}
